import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Clear auth and redirect to login
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private extractErrorMessage(error: unknown): string {
    const visited = new Set<unknown>();

    const read = (value: unknown): string | null => {
      if (!value || visited.has(value)) return null;
      if (typeof value === 'string') return value;
      if (value instanceof Error) return value.message;
      if (typeof value !== 'object') return null;

      visited.add(value);

      const record = value as Record<string, unknown>;
      return (
        read(record.error) ||
        read(record.message) ||
        read(record.details) ||
        read(record.msg) ||
        null
      );
    };

    return read(error) || 'Upload failed';
  }

  private chunkFiles(files: File[], maxFilesPerChunk = 10, maxBytesPerChunk = 75 * 1024 * 1024) {
    const chunks: File[][] = [];
    let currentChunk: File[] = [];
    let currentBytes = 0;

    for (const file of files) {
      const wouldExceedFileCount = currentChunk.length >= maxFilesPerChunk;
      const wouldExceedBytes = currentChunk.length > 0 && currentBytes + file.size > maxBytesPerChunk;

      if (wouldExceedFileCount || wouldExceedBytes) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentBytes = 0;
      }

      currentChunk.push(file);
      currentBytes += file.size;
    }

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }

    return chunks;
  }

  private async uploadFilesInChunks(
    url: string,
    files: File[],
    options?: {
      onProgress?: (progress: number) => void;
      extraFieldsForChunk?: (chunkIndex: number) => Record<string, string | null | undefined>;
      maxFilesPerChunk?: number;
      maxBytesPerChunk?: number;
      retriesPerChunk?: number;
    }
  ) {
    const chunks = this.chunkFiles(
      files,
      options?.maxFilesPerChunk ?? 10,
      options?.maxBytesPerChunk ?? 75 * 1024 * 1024
    );
    const totalBytes = Math.max(files.reduce((sum, file) => sum + (file.size || 0), 0), 1);
    const batchId = url.split('/').filter(Boolean).pop() || 'unknown';
    let uploadedBytes = 0;
    let chunkStartIndex = 0;

    console.info('[UPLOAD] Starting chunked upload', {
      batchId,
      chunkCount: chunks.length,
      fileCount: files.length,
      totalBytes,
    });

    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
      const chunk = chunks[chunkIndex];
      const chunkBytes = chunk.reduce((sum, file) => sum + (file.size || 0), 0);
      let attempt = 0;
      const maxAttempts = (options?.retriesPerChunk ?? 2) + 1;

      while (attempt < maxAttempts) {
        try {
          const formData = new FormData();
          chunk.forEach((file) => formData.append('images', file));
          formData.append('chunkIndex', String(chunkIndex));
          formData.append('totalChunks', String(chunks.length));
          formData.append('chunkStartIndex', String(chunkStartIndex));
          const uploadedBytesBeforeChunk = uploadedBytes;

          const extraFields = options?.extraFieldsForChunk?.(chunkIndex) || {};
          Object.entries(extraFields).forEach(([key, value]) => {
            if (value != null) {
              formData.append(key, value);
            }
          });

          console.info('[UPLOAD] Sending chunk', {
            batchId,
            chunkIndex,
            chunkNumber: chunkIndex + 1,
            totalChunks: chunks.length,
            attempt: attempt + 1,
            fileCount: chunk.length,
            chunkStartIndex,
            chunkBytes,
          });

          await this.api.post(url, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (event: any) => {
              if (!options?.onProgress) return;
              const chunkLoaded = Math.min(event.loaded || 0, chunkBytes);
              const overall = Math.round(((uploadedBytesBeforeChunk + chunkLoaded) * 100) / totalBytes);
              options.onProgress(Math.max(0, Math.min(100, overall)));
            },
          });

          uploadedBytes += chunkBytes;
          chunkStartIndex += chunk.length;
          options?.onProgress?.(Math.round((uploadedBytes * 100) / totalBytes));

          console.info('[UPLOAD] Chunk complete', {
            batchId,
            chunkIndex,
            chunkNumber: chunkIndex + 1,
            totalChunks: chunks.length,
            uploadedBytes,
            totalBytes,
          });

          break;
        } catch (error: any) {
          attempt += 1;
          const isAxios = axios.isAxiosError(error);
          const failureType = isAxios
            ? (error.response ? 'server-response' : 'network-or-browser')
            : 'unknown';

          console.error('[UPLOAD] Chunk upload failed', {
            batchId,
            chunkIndex,
            chunkNumber: chunkIndex + 1,
            totalChunks: chunks.length,
            attempt,
            maxAttempts,
            failureType,
            status: error?.response?.status,
            responseData: error?.response?.data,
            message: error?.message,
          });

          if (attempt >= maxAttempts) {
            const chunkNumber = chunkIndex + 1;
            const totalChunks = chunks.length;
            const baseMessage = this.extractErrorMessage(error?.response?.data) || this.extractErrorMessage(error);
            const wrappedError = new Error(`Upload failed on chunk ${chunkNumber} of ${totalChunks}. ${baseMessage}`);
            (wrappedError as any).response = error?.response;
            throw wrappedError;
          }
        }
      }
    }
  }

  // Auth methods
  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
    }
    return response.data;
  }

  async register(data: { email: string; password: string; fullName: string; companyName?: string; phone: string; firebaseIdToken: string }) {
    const response = await this.api.post('/auth/register', data);
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
    }
    return response.data;
  }

  async verifyPhone(idToken: string) {
    const response = await this.api.post('/auth/verify-phone', { idToken });
    return response.data;
  }

  async getCurrentUser() {
    const response = await this.api.get('/auth/me');
    return response.data.user;
  }

  async logout() {
    await this.api.post('/auth/logout');
    localStorage.removeItem('authToken');
  }

  async forgotPassword(email: string) {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string) {
    const response = await this.api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  }

  async updateUserSettings(settings: {
    exportFormat?: 'jpg' | 'png';
    exportQuality?: number;
    defaultPositioningMode?: 'auto' | 'manual';
    reflectionEnabled?: boolean;
    reflectionIntensity?: number;
    reflectionBrightness?: number;
    reflectionOffset?: number;
    reflectionBlur?: number;
    shadowEnabled?: boolean;
    shadowOpacity?: number;
    shadowWidth?: number;
    shadowBlur?: number;
  }) {
    const response = await this.api.patch('/auth/settings', settings);
    return response.data;
  }

  // Batch operations
  async initBatch(name: string, imageCount: number, settings?: any) {
    const response = await this.api.post('/upload/init-batch', {
      name,
      imageCount,
      settings,
    });
    return response.data;
  }

  async uploadImages(batchId: string, files: File[]) {
    await this.uploadFilesInChunks(`/upload/batch/${batchId}`, files);
    return { success: true };
  }

  async uploadExteriorBatch(batchId: string, files: File[], options?: {
    backgroundImage?: string | null;
    onProgress?: (progress: number) => void;
  }) {
    await this.uploadFilesInChunks(`/upload/batch/${batchId}`, files, {
      onProgress: options?.onProgress,
      extraFieldsForChunk: (chunkIndex) => (
        chunkIndex === 0 && options?.backgroundImage
          ? { backgroundImage: options.backgroundImage }
          : {}
      ),
    });
    return { success: true };
  }


  async getBatchStatus(batchId: string) {
    const response = await this.api.get(`/upload/batch/${batchId}/status`);
    return response.data;
  }

  async getBatchHistory(page = 1, limit = 10, status?: string, sortBy?: string, sortDir?: string, type?: string) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (status) params.append('status', status);
    if (sortBy) params.append('sortBy', sortBy);
    if (sortDir) params.append('sortDir', sortDir);
    if (type) params.append('type', type);

    const response = await this.api.get(`/batch/history?${params}`);
    return response.data;
  }

  async getBatchDetails(batchId: string) {
    const response = await this.api.get(`/batch/${batchId}`);
    return response.data.batch;
  }

  async downloadBatch(batchId: string) {
    const response = await this.api.get(`/batch/${batchId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteBatch(batchId: string) {
    const response = await this.api.delete(`/batch/${batchId}`);
    return response.data;
  }

  async saveBatch(batchId: string) {
    const response = await this.api.post(`/batch/${batchId}/save`);
    return response.data;
  }

  async unsaveBatch(batchId: string) {
    const response = await this.api.delete(`/batch/${batchId}/save`);
    return response.data;
  }

  async renameBatch(batchId: string, name: string) {
    const response = await this.api.patch(`/batch/${batchId}/rename`, { name });
    return response.data;
  }

  // Subscription methods
  async getSubscriptionTiers() {
    const response = await this.api.get('/subscription/tiers');
    return response.data.tiers;
  }

  async getSubscriptionStatus() {
    const response = await this.api.get('/subscription/status');
    return response.data.subscription;
  }

  async createCheckoutSession(tier: string) {
    const response = await this.api.post('/subscription/create-checkout-session', { tier });
    return response.data;
  }

  async verifySession(sessionId: string) {
    const response = await this.api.post('/subscription/verify-session', { sessionId });
    return response.data;
  }

  async cancelSubscription() {
    const response = await this.api.post('/subscription/cancel');
    return response.data;
  }

  // Processing methods
  async getBackgrounds(category?: string) {
    const params = category ? `?category=${category}` : '';
    const response = await this.api.get(`/processing/backgrounds${params}`);
    return response.data;
  }

  async uploadBackground(payload: {
    name: string;
    type: 'image';
    value: string;
    category?: string;
    thumbnailUrl?: string;
    metadata?: {
      width?: number;
      height?: number;
      fileSize?: number;
      mimeType?: string;
    };
  }) {
    const response = await this.api.post('/processing/backgrounds/upload', payload);
    return response.data;
  }

  async deleteBackground(backgroundId: string) {
    const response = await this.api.delete(`/processing/backgrounds/${backgroundId}`);
    return response.data;
  }

  async getPresets() {
    const response = await this.api.get('/processing/presets');
    return response.data.presets;
  }

  async getQueueStatus() {
    const response = await this.api.get('/processing/queue/status');
    return response.data;
  }

  // Expose post method for other uses
  async post(url: string, data: any, config?: AxiosRequestConfig) {
    const response = await this.api.post(url, data, config);
    return response;
  }

  // AI Background Generation
  async generateAiBackground(prompt: string, aspectRatio?: string, seed?: number) {
    const response = await this.api.post('/ai/generate-background', {
      prompt,
      aspectRatio: aspectRatio || '4:3',
      seed,
    });
    return response.data;
  }

  async getAiQuota() {
    const response = await this.api.get('/ai/quota');
    return response.data;
  }

  // Interior Processing
  async initInteriorBatch(name: string, imageCount: number, settings?: any) {
    const response = await this.api.post('/interior/init-batch', {
      name,
      imageCount,
      settings,
    });
    return response.data;
  }

  async uploadInteriorBatch(batchId: string, files: File[], onProgress?: (progress: number) => void) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });
    const response = await this.api.post(`/interior/batch/${batchId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e: any) => {
        if (onProgress) {
          onProgress(Math.round(((e.loaded || 0) * 100) / (e.total || 1)));
        }
      },
    });
    return response.data;
  }

  // Background Cleanup
  async cleanupBackground(image: string, options: { desnow: boolean; deshadow: boolean; dereflection: boolean }) {
    const response = await this.api.post('/cleanup/process', { image, options });
    return response.data;
  }

  // Preview Generation (same pipeline as batch processing)
  async generatePreview(image: string, settings: { background: any; positioning: any; effects: any }) {
    const response = await this.api.post('/preview/generate', {
      image,
      background: settings.background,
      positioning: settings.positioning,
      effects: settings.effects,
    });
    return response.data;
  }

  // Background Removal
  async removeBackground(imageBase64: string, options?: { blurWindows?: boolean; windowBlurIntensity?: number }) {
    const response = await this.api.post('/remove-background/remove', {
      image: imageBase64,
      ...(options?.blurWindows && { blur_windows: true }),
      ...(options?.windowBlurIntensity != null && { blur_intensity: options.windowBlurIntensity }),
    });
    return response.data;
  }

  async removeBatchBackgrounds(images: string[], options?: { blurWindows?: boolean; windowBlurIntensity?: number }) {
    const response = await this.api.post('/remove-background/remove-batch', {
      images,
      ...(options?.blurWindows && { blur_windows: true }),
      ...(options?.windowBlurIntensity != null && { blur_intensity: options.windowBlurIntensity }),
    });
    return response.data;
  }

  // Support methods
  async sendSupportChat(message: string, history: { role: string; content: string }[]) {
    const response = await this.api.post('/support/chat', { message, history });
    return response.data;
  }

  async createSupportTicket(subject: string, category: string, message: string, aiConversation?: { role: string; content: string }[]) {
    const response = await this.api.post('/support/ticket', { subject, category, message, aiConversation });
    return response.data;
  }

  async getSupportTickets() {
    const response = await this.api.get('/support/tickets');
    return response.data;
  }
}

export default new ApiService();
